import { lazy } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { LoginPage } from "@/modules/auth/LoginPage";
import { AppShell } from "@/shared/components/AppShell";
import { RouteGuard } from "@/shared/components/RouteGuard";
import { useAuth } from "@/shared/hooks/useAuth";

// Sayfalar lazy yüklenir: her route kendi chunk'ı olur, böylece hafif sayfaların ilk
// yüklemesi ağır editör/grafik bağımlılıklarını (tiptap/katex/recharts/dnd-kit) çekmez.
// Tümü named export olduğundan default'a eşleme gerekir. LoginPage AppShell Suspense'inin
// dışında (/login) kaldığından eager bırakılır.
const DashboardPage = lazy(() => import("@/modules/dashboard/DashboardPage").then((m) => ({ default: m.DashboardPage })));
const AnalyticsPage = lazy(() => import("@/modules/analytics/AnalyticsPage").then((m) => ({ default: m.AnalyticsPage })));
const HardestQuestionsPage = lazy(() => import("@/modules/analytics/HardestQuestionsPage").then((m) => ({ default: m.HardestQuestionsPage })));
const AppUsagePage = lazy(() => import("@/modules/analytics/AppUsagePage").then((m) => ({ default: m.AppUsagePage })));
const PublisherStudentsPage = lazy(() => import("@/modules/publisher-students/PublisherStudentsPage").then((m) => ({ default: m.PublisherStudentsPage })));
const SchoolsHubPage = lazy(() => import("@/modules/schools/SchoolsHubPage").then((m) => ({ default: m.SchoolsHubPage })));
const SchoolManagementPage = lazy(() => import("@/modules/school-management/SchoolManagementPage").then((m) => ({ default: m.SchoolManagementPage })));
const ClassroomManagePage = lazy(() => import("@/modules/classrooms/ClassroomDetailPage").then((m) => ({ default: m.ClassroomDetailPage })));
const GuidancePage = lazy(() => import("@/modules/guidance/GuidancePage").then((m) => ({ default: m.GuidancePage })));
const ClassroomDetailPage = lazy(() => import("@/modules/guidance/ClassroomDetailPage").then((m) => ({ default: m.ClassroomDetailPage })));
const StudentDetailPage = lazy(() => import("@/modules/guidance/StudentDetailPage").then((m) => ({ default: m.StudentDetailPage })));
const StudentMeetingFormPage = lazy(() => import("@/modules/guidance/StudentMeetingFormPage").then((m) => ({ default: m.StudentMeetingFormPage })));
const GuidanceReportPreviewPage = lazy(() => import("@/modules/guidance/parent-report/GuidanceReportPreviewPage").then((m) => ({ default: m.GuidanceReportPreviewPage })));
const GuidanceReportPage = lazy(() => import("@/modules/guidance/parent-report/GuidanceReportPage").then((m) => ({ default: m.GuidanceReportPage })));
const UserListPage = lazy(() => import("@/modules/users/UserListPage").then((m) => ({ default: m.UserListPage })));
const AiUploadPage = lazy(() => import("@/modules/ai-upload/AiUploadPage").then((m) => ({ default: m.AiUploadPage })));
const SmsPage = lazy(() => import("@/modules/sms/SmsPage").then((m) => ({ default: m.SmsPage })));
const ScheduleListPage = lazy(() => import("@/modules/schedule/ScheduleListPage").then((m) => ({ default: m.ScheduleListPage })));
const ScheduleWizardPage = lazy(() => import("@/modules/schedule/ScheduleWizardPage").then((m) => ({ default: m.ScheduleWizardPage })));
const CreditManagementPage = lazy(() => import("@/modules/credit-management/CreditManagementPage").then((m) => ({ default: m.CreditManagementPage })));
const StoreSalesPage = lazy(() => import("@/modules/store/StoreSalesPage").then((m) => ({ default: m.StoreSalesPage })));
const DefinedPackagesPage = lazy(() => import("@/modules/defined-packages/DefinedPackagesPage").then((m) => ({ default: m.DefinedPackagesPage })));
const PackagesPage = lazy(() => import("@/modules/packages/PackagesPage").then((m) => ({ default: m.PackagesPage })));
const VideoSolutionsPage = lazy(() => import("@/modules/video-solutions/VideoSolutionsPage").then((m) => ({ default: m.VideoSolutionsPage })));
const DailyQuestionsPage = lazy(() => import("@/modules/daily-questions/DailyQuestionsPage").then((m) => ({ default: m.DailyQuestionsPage })));
const MascotsPage = lazy(() => import("@/modules/mascots/MascotsPage").then((m) => ({ default: m.MascotsPage })));
const PackageDetailPage = lazy(() => import("@/modules/packages/PackageDetailPage").then((m) => ({ default: m.PackageDetailPage })));
const CourseLandingPage = lazy(() => import("@/modules/packages/CourseLandingPage").then((m) => ({ default: m.CourseLandingPage })));
const CourseLectureBooksPage = lazy(() => import("@/modules/packages/CourseLectureBooksPage").then((m) => ({ default: m.CourseLectureBooksPage })));
const CourseLectureBookPage = lazy(() => import("@/modules/packages/CourseLectureBookPage").then((m) => ({ default: m.CourseLectureBookPage })));
const CourseBooksPage = lazy(() => import("@/modules/packages/CourseBooksPage").then((m) => ({ default: m.CourseBooksPage })));
const UnitQuestionsPage = lazy(() => import("@/modules/packages/UnitQuestionsPage").then((m) => ({ default: m.UnitQuestionsPage })));
const ExamBooksPage = lazy(() => import("@/modules/packages/ExamBooksPage").then((m) => ({ default: m.ExamBooksPage })));
const ExamBookQuestionsPage = lazy(() => import("@/modules/packages/ExamBookQuestionsPage").then((m) => ({ default: m.ExamBookQuestionsPage })));
const ProfilePage = lazy(() => import("@/modules/profile/ProfilePage").then((m) => ({ default: m.ProfilePage })));
const PublisherPaperExamsPage = lazy(() => import("@/modules/paper-exams/PublisherPaperExamsPage").then((m) => ({ default: m.PublisherPaperExamsPage })));
const PublisherMessagesPage = lazy(() => import("@/modules/publisher-messages/PublisherMessagesPage").then((m) => ({ default: m.PublisherMessagesPage })));
const PublisherSettingsHubPage = lazy(() => import("@/modules/publisher-settings/PublisherSettingsHubPage").then((m) => ({ default: m.PublisherSettingsHubPage })));
const SchoolPaperExamsPage = lazy(() => import("@/modules/paper-exams/SchoolPaperExamsPage").then((m) => ({ default: m.SchoolPaperExamsPage })));

// SYSTEM_ADMIN kapsayıcı sayfaları: eskiden ayrı 11 menü öğesi olan ekranlar, sekmeli
// gruplar altında birleştirildi (menü sadeleştirme). Her kapsayıcı alt sayfaları kendi
// içinde lazy tutar; eski yollar aşağıda `?sec=` hedefine yönlendirilir.
const PublishersCreditsPage = lazy(() => import("@/modules/system-admin/PublishersCreditsPage").then((m) => ({ default: m.PublishersCreditsPage })));
const ContentPage = lazy(() => import("@/modules/system-admin/ContentPage").then((m) => ({ default: m.ContentPage })));
const AiHubPage = lazy(() => import("@/modules/system-admin/AiHubPage").then((m) => ({ default: m.AiHubPage })));
const SystemHubPage = lazy(() => import("@/modules/system-admin/SystemHubPage").then((m) => ({ default: m.SystemHubPage })));
const AiConsumptionPage = lazy(() => import("@/modules/ai-usage-owner/AiConsumptionPage").then((m) => ({ default: m.AiConsumptionPage })));

// Tahta Modu (öğretmen akıllı tahta projeksiyon)
const TahtaShell = lazy(() => import("@/modules/tahta/TahtaShell").then((m) => ({ default: m.TahtaShell })));
const TahtaPackagesPage = lazy(() => import("@/modules/tahta/TahtaPackagesPage").then((m) => ({ default: m.TahtaPackagesPage })));
const TahtaPackageLayout = lazy(() => import("@/modules/tahta/TahtaPackageLayout").then((m) => ({ default: m.TahtaPackageLayout })));
const TahtaPackageIndex = lazy(() => import("@/modules/tahta/TahtaPackageLayout").then((m) => ({ default: m.TahtaPackageIndex })));
const TahtaTopicPage = lazy(() => import("@/modules/tahta/TahtaTopicPage").then((m) => ({ default: m.TahtaTopicPage })));
const TahtaQuestionPage = lazy(() => import("@/modules/tahta/TahtaQuestionPage").then((m) => ({ default: m.TahtaQuestionPage })));

// Muhasebe / Kayıt modülü
const MuhasebePage = lazy(() => import("@/modules/muhasebe/MuhasebePage").then((m) => ({ default: m.MuhasebePage })));
const ContractWizardPage = lazy(() => import("@/modules/muhasebe/pages/ContractWizardPage").then((m) => ({ default: m.ContractWizardPage })));
const StudentAccountDetailPage = lazy(() => import("@/modules/muhasebe/pages/StudentAccountDetailPage").then((m) => ({ default: m.StudentAccountDetailPage })));
const ContractDetailPage = lazy(() => import("@/modules/muhasebe/pages/ContractDetailPage").then((m) => ({ default: m.ContractDetailPage })));
const EmploymentWizardPage = lazy(() => import("@/modules/muhasebe/pages/EmploymentWizardPage").then((m) => ({ default: m.EmploymentWizardPage })));
const TeacherAccountDetailPage = lazy(() => import("@/modules/muhasebe/pages/TeacherAccountDetailPage").then((m) => ({ default: m.TeacherAccountDetailPage })));
const EmploymentContractDetailPage = lazy(() => import("@/modules/muhasebe/pages/EmploymentContractDetailPage").then((m) => ({ default: m.EmploymentContractDetailPage })));

/** Yalnız Muhasebeci → /muhasebe, yalnız Öğretmen → /tahta, diğerleri → dashboard. */
const HomeRoute = () => {
  const { hasAnyRole } = useAuth();
  const adminRoles = ["SCHOOL_ADMIN", "SYSTEM_ADMIN", "GUIDANCE_COUNSELOR", "PUBLISHER_ADMIN"] as const;
  const teacherOnly = hasAnyRole(["TEACHER"]) && !hasAnyRole([...adminRoles, "ACCOUNTANT"]);
  if (teacherOnly) return <Navigate to="/tahta" replace />;
  const accountantOnly = hasAnyRole(["ACCOUNTANT"]) && !hasAnyRole([...adminRoles]);
  return accountantOnly ? <Navigate to="/muhasebe" replace /> : <DashboardPage />;
};

/** Eski /publishers-credits linkleri (bookmark/mail) → /publishers; ?sec= sorgusu korunur. */
const LegacyPublishersCreditsRedirect = () => {
  const { search } = useLocation();
  return <Navigate to={`/publishers${search}`} replace />;
};

export const App = () => (
  <Routes>
    <Route path="/login" element={<LoginPage />} />

    <Route
      element={
        <RouteGuard>
          <AppShell />
        </RouteGuard>
      }
    >
      <Route index element={<HomeRoute />} />
      <Route
        path="analiz"
        element={
          <RouteGuard roles={["PUBLISHER_ADMIN"]}>
            <AnalyticsPage />
          </RouteGuard>
        }
      />
      <Route
        path="uygulama-analizi"
        element={
          <RouteGuard roles={["PUBLISHER_ADMIN"]}>
            <AppUsagePage />
          </RouteGuard>
        }
      />
      <Route
        path="zorlanilan-sorular"
        element={
          <RouteGuard roles={["PUBLISHER_ADMIN"]}>
            <HardestQuestionsPage />
          </RouteGuard>
        }
      />
      {/* Menüde yer almaz — ana sayfadaki "Aktif öğrenci" kartındaki toplam sayıdan ulaşılır
          (aynı kalıp: /zorlanilan-sorular). Öğrenci detayı ayrı rota değil, liste üstünde
          açılan bir penceredir. */}
      <Route
        path="ogrenciler"
        element={
          <RouteGuard roles={["PUBLISHER_ADMIN"]}>
            <PublisherStudentsPage />
          </RouteGuard>
        }
      />
      <Route
        path="schools"
        element={
          <RouteGuard roles={["SYSTEM_ADMIN"]}>
            <SchoolsHubPage />
          </RouteGuard>
        }
      />

      {/* --- SYSTEM_ADMIN sekmeli kapsayıcı grupları --- */}
      <Route
        path="publishers"
        element={
          <RouteGuard roles={["SYSTEM_ADMIN"]}>
            <PublishersCreditsPage />
          </RouteGuard>
        }
      />
      <Route
        path="content"
        element={
          <RouteGuard roles={["SYSTEM_ADMIN"]}>
            <ContentPage />
          </RouteGuard>
        }
      />
      <Route
        path="ai-usage"
        element={
          <RouteGuard roles={["SYSTEM_ADMIN"]}>
            <AiHubPage />
          </RouteGuard>
        }
      />
      <Route
        path="system"
        element={
          <RouteGuard roles={["SYSTEM_ADMIN"]}>
            <SystemHubPage />
          </RouteGuard>
        }
      />

      {/* Eski SYSTEM_ADMIN yolları → yeni kapsayıcı + sekme (bookmark/mail linkleri kırılmasın). */}
      <Route path="publishers-credits" element={<LegacyPublishersCreditsRedirect />} />
      <Route path="credit-movements" element={<Navigate to="/publishers?sec=credits" replace />} />
      <Route path="definitions" element={<Navigate to="/content?sec=definitions" replace />} />
      <Route path="question-pool" element={<Navigate to="/content?sec=pool" replace />} />
      <Route path="ai-management" element={<Navigate to="/ai-usage?sec=usage" replace />} />
      <Route path="ai-prompts" element={<Navigate to="/ai-usage?sec=prompts" replace />} />
      {/* "Ayarlar" grubu "Sistem" altında birleşti: eski /settings + alt sekmeleri /system'e taşındı. */}
      <Route path="settings" element={<Navigate to="/system?sec=email" replace />} />
      <Route path="auth-settings" element={<Navigate to="/system?sec=email" replace />} />
      <Route path="mobile-settings" element={<Navigate to="/system?sec=giris" replace />} />
      <Route path="scheduled-jobs" element={<Navigate to="/system?sec=jobs" replace />} />
      <Route path="robot-students" element={<Navigate to="/system?sec=robots" replace />} />
      <Route path="messages" element={<Navigate to="/system?sec=messages" replace />} />

      <Route
        path="okul-yonetimi"
        element={
          <RouteGuard roles={["SCHOOL_ADMIN", "GUIDANCE_COUNSELOR"]}>
            <SchoolManagementPage />
          </RouteGuard>
        }
      />
      {/* Eski ayrı liste yolları → birleşik Okul Yönetimi (ilgili sekme açık). */}
      <Route path="classrooms" element={<Navigate to="/okul-yonetimi?tab=classrooms" replace />} />
      <Route path="students" element={<Navigate to="/okul-yonetimi?tab=students" replace />} />
      <Route path="teachers" element={<Navigate to="/okul-yonetimi?tab=teachers" replace />} />
      <Route
        path="classrooms/:classroomId"
        element={
          <RouteGuard roles={["SCHOOL_ADMIN", "GUIDANCE_COUNSELOR"]}>
            <ClassroomManagePage />
          </RouteGuard>
        }
      />
      <Route
        path="guidance"
        element={
          <RouteGuard roles={["SCHOOL_ADMIN", "GUIDANCE_COUNSELOR"]}>
            <GuidancePage />
          </RouteGuard>
        }
      />
      <Route
        path="guidance/classrooms/:classroomId"
        element={
          <RouteGuard roles={["SCHOOL_ADMIN", "GUIDANCE_COUNSELOR"]}>
            <ClassroomDetailPage />
          </RouteGuard>
        }
      />
      <Route
        path="guidance/students/:studentId"
        element={
          <RouteGuard roles={["SCHOOL_ADMIN", "GUIDANCE_COUNSELOR"]}>
            <StudentDetailPage />
          </RouteGuard>
        }
      />
      <Route
        path="guidance/students/:studentId/meetings/new"
        element={
          <RouteGuard roles={["SCHOOL_ADMIN", "GUIDANCE_COUNSELOR"]}>
            <StudentMeetingFormPage />
          </RouteGuard>
        }
      />
      <Route
        path="guidance/students/:studentId/meetings/:meetingId/edit"
        element={
          <RouteGuard roles={["SCHOOL_ADMIN", "GUIDANCE_COUNSELOR"]}>
            <StudentMeetingFormPage />
          </RouteGuard>
        }
      />
      <Route
        path="guidance/students/:studentId/parent-report/new"
        element={
          <RouteGuard roles={["SCHOOL_ADMIN", "GUIDANCE_COUNSELOR"]}>
            <GuidanceReportPreviewPage />
          </RouteGuard>
        }
      />
      <Route
        path="guidance/reports/:reportId"
        element={
          <RouteGuard roles={["SCHOOL_ADMIN", "GUIDANCE_COUNSELOR"]}>
            <GuidanceReportPage />
          </RouteGuard>
        }
      />
      <Route
        path="users"
        element={
          <RouteGuard roles={["SCHOOL_ADMIN"]}>
            <UserListPage />
          </RouteGuard>
        }
      />
      <Route
        path="ai-upload"
        element={
          <RouteGuard roles={["SCHOOL_ADMIN"]}>
            <AiUploadPage />
          </RouteGuard>
        }
      />
      <Route
        path="mesaj-gonderim"
        element={
          <RouteGuard roles={["SCHOOL_ADMIN"]}>
            <SmsPage />
          </RouteGuard>
        }
      />
      <Route
        path="ders-programi"
        element={
          <RouteGuard roles={["SCHOOL_ADMIN"]}>
            <ScheduleListPage />
          </RouteGuard>
        }
      />
      <Route
        path="ders-programi/:sessionId/adim/:step"
        element={
          <RouteGuard roles={["SCHOOL_ADMIN"]}>
            <ScheduleWizardPage />
          </RouteGuard>
        }
      />
      <Route
        path="credit-management"
        element={
          <RouteGuard roles={["PUBLISHER_ADMIN"]}>
            <CreditManagementPage />
          </RouteGuard>
        }
      />
      {/* Uygulama içi satış analitiği — yayınevi yalnız kendi paketlerini görür (kapsam sunucuda). */}
      <Route
        path="sales"
        element={
          <RouteGuard roles={["PUBLISHER_ADMIN"]}>
            <StoreSalesPage scope="publisher" />
          </RouteGuard>
        }
      />
      <Route
        path="ai-kullanim"
        element={
          <RouteGuard roles={["PUBLISHER_ADMIN", "SCHOOL_ADMIN", "GUIDANCE_COUNSELOR"]}>
            <AiConsumptionPage />
          </RouteGuard>
        }
      />
      <Route
        path="defined-packages"
        element={
          <RouteGuard roles={["SCHOOL_ADMIN", "GUIDANCE_COUNSELOR"]}>
            <DefinedPackagesPage />
          </RouteGuard>
        }
      />
      <Route
        path="packages"
        element={
          <RouteGuard roles={["PUBLISHER_ADMIN"]}>
            <PackagesPage />
          </RouteGuard>
        }
      />
      <Route
        path="video-solutions"
        element={
          <RouteGuard roles={["PUBLISHER_ADMIN"]}>
            <VideoSolutionsPage />
          </RouteGuard>
        }
      />
      <Route
        path="maskotlar"
        element={
          <RouteGuard roles={["PUBLISHER_ADMIN"]}>
            <MascotsPage />
          </RouteGuard>
        }
      />
      <Route
        path="gunun-sorusu"
        element={
          <RouteGuard roles={["PUBLISHER_ADMIN"]}>
            <DailyQuestionsPage />
          </RouteGuard>
        }
      />
      <Route
        path="packages/:packageId"
        element={
          <RouteGuard roles={["PUBLISHER_ADMIN"]}>
            <PackageDetailPage />
          </RouteGuard>
        }
      />
      <Route
        path="packages/:packageId/courses/:courseId"
        element={
          <RouteGuard roles={["PUBLISHER_ADMIN"]}>
            <CourseLandingPage />
          </RouteGuard>
        }
      />
      <Route
        path="packages/:packageId/courses/:courseId/lectures"
        element={
          <RouteGuard roles={["PUBLISHER_ADMIN"]}>
            <CourseLectureBooksPage />
          </RouteGuard>
        }
      />
      <Route
        path="packages/:packageId/courses/:courseId/lectures/:bookId"
        element={
          <RouteGuard roles={["PUBLISHER_ADMIN"]}>
            <CourseLectureBookPage />
          </RouteGuard>
        }
      />
      <Route
        path="packages/:packageId/courses/:courseId/books"
        element={
          <RouteGuard roles={["PUBLISHER_ADMIN"]}>
            <CourseBooksPage />
          </RouteGuard>
        }
      />
      <Route
        path="packages/:packageId/courses/:courseId/units/:unitId"
        element={
          <RouteGuard roles={["PUBLISHER_ADMIN"]}>
            <UnitQuestionsPage />
          </RouteGuard>
        }
      />
      <Route
        path="packages/:packageId/exams/:packageExamId"
        element={
          <RouteGuard roles={["PUBLISHER_ADMIN"]}>
            <ExamBooksPage />
          </RouteGuard>
        }
      />
      <Route
        path="packages/:packageId/exams/:packageExamId/books/:bookId"
        element={
          <RouteGuard roles={["PUBLISHER_ADMIN"]}>
            <ExamBookQuestionsPage />
          </RouteGuard>
        }
      />
      {/* Sınav bağlamı birleşik kitap editörü (Soru Bankası / Tarama Testi; çok dersli). */}
      <Route
        path="packages/:packageId/exams/:packageExamId/lecture-books/:bookId"
        element={
          <RouteGuard roles={["PUBLISHER_ADMIN"]}>
            <CourseLectureBookPage />
          </RouteGuard>
        }
      />
      <Route
        path="optik-denemeler"
        element={
          <RouteGuard roles={["PUBLISHER_ADMIN"]}>
            <PublisherPaperExamsPage />
          </RouteGuard>
        }
      />
      <Route
        path="denemeler"
        element={
          <RouteGuard roles={["SCHOOL_ADMIN", "GUIDANCE_COUNSELOR"]}>
            <SchoolPaperExamsPage />
          </RouteGuard>
        }
      />
      {/* --- Yayınevi: gönderilen e-postalar + kendi ayarları --- */}
      <Route
        path="yayinevi-mesajlar"
        element={
          <RouteGuard roles={["PUBLISHER_ADMIN"]}>
            <PublisherMessagesPage />
          </RouteGuard>
        }
      />
      <Route
        path="yayinevi-ayarlari"
        element={
          <RouteGuard roles={["PUBLISHER_ADMIN"]}>
            <PublisherSettingsHubPage />
          </RouteGuard>
        }
      />
      {/* --- Muhasebe / Kayıt (Okul Yöneticisi + Muhasebeci) --- */}
      <Route
        path="muhasebe"
        element={
          <RouteGuard roles={["SCHOOL_ADMIN", "ACCOUNTANT"]}>
            <MuhasebePage />
          </RouteGuard>
        }
      />
      <Route
        path="muhasebe/kayit/yeni"
        element={
          <RouteGuard roles={["SCHOOL_ADMIN", "ACCOUNTANT"]}>
            <ContractWizardPage />
          </RouteGuard>
        }
      />
      <Route
        path="muhasebe/kayit/:contractId/adim/:step"
        element={
          <RouteGuard roles={["SCHOOL_ADMIN", "ACCOUNTANT"]}>
            <ContractWizardPage />
          </RouteGuard>
        }
      />
      <Route
        path="muhasebe/ogrenci/:studentId"
        element={
          <RouteGuard roles={["SCHOOL_ADMIN", "ACCOUNTANT"]}>
            <StudentAccountDetailPage />
          </RouteGuard>
        }
      />
      <Route
        path="muhasebe/sozlesme/:contractId"
        element={
          <RouteGuard roles={["SCHOOL_ADMIN", "ACCOUNTANT"]}>
            <ContractDetailPage />
          </RouteGuard>
        }
      />
      <Route
        path="muhasebe/istihdam/yeni"
        element={
          <RouteGuard roles={["SCHOOL_ADMIN", "ACCOUNTANT"]}>
            <EmploymentWizardPage />
          </RouteGuard>
        }
      />
      <Route
        path="muhasebe/istihdam/:contractId/adim/:step"
        element={
          <RouteGuard roles={["SCHOOL_ADMIN", "ACCOUNTANT"]}>
            <EmploymentWizardPage />
          </RouteGuard>
        }
      />
      <Route
        path="muhasebe/ogretmen/:teacherId"
        element={
          <RouteGuard roles={["SCHOOL_ADMIN", "ACCOUNTANT"]}>
            <TeacherAccountDetailPage />
          </RouteGuard>
        }
      />
      <Route
        path="muhasebe/is-sozlesmesi/:contractId"
        element={
          <RouteGuard roles={["SCHOOL_ADMIN", "ACCOUNTANT"]}>
            <EmploymentContractDetailPage />
          </RouteGuard>
        }
      />
      <Route path="profile" element={<ProfilePage />} />
    </Route>

    {/* --- Tahta Modu (AppShell dışı, kendi layout'u) --- */}
    <Route
      element={
        <RouteGuard roles={["TEACHER"]}>
          <TahtaShell />
        </RouteGuard>
      }
    >
      <Route path="tahta" element={<TahtaPackagesPage />} />
      <Route path="tahta/packages/:packageId" element={<TahtaPackageLayout />}>
        <Route index element={<TahtaPackageIndex />} />
        <Route path="topics/:topicId" element={<TahtaTopicPage />} />
        <Route path="topics/:topicId/tests/:testId" element={<TahtaQuestionPage />} />
      </Route>
    </Route>

    <Route path="*" element={<Navigate to="/" replace />} />
  </Routes>
);
